import { useEffect } from "react";
import { useSetAtom } from "jotai";
import tracking from "frontend/tracking";
import styles from "./template-selection.module.css";
import CloseIcon from "frontend/ui-components/design-system/icons/close-icon";
import { templatesIcon } from "frontend/canvas-designer-new/sidebar/side-menu-items/side-menu-icons";
import { hoveredTemplatePreviewAtom, type TemplatePreviewBoundaries } from "state-atoms/stage-atoms";
import { TEMPLATE_SELECTION_CARDS, loadTemplate, useApplyTemplate, useLoadTemplates } from "./utils";
import { ModalType } from "frontend/modals/modal-manager";
import useModalDeepLink from "frontend/hooks/use-modal-deep-link";
import TemplateSelectionCard from "./template-selection-card";
import { SidepanelType, sidePanelType } from "state-atoms";
import AISelectionCard from "./ai-selection-card";

const TEMPLATE_BOUNDARIES: TemplatePreviewBoundaries = {
  topSelector: '[id="onboarding-cim-templates-modal"]',
  bottomSelector: '[id="tools-sidemenu"]',
  defaultTopOffset: 75,
  defaultBottomOffset: 60,
};

interface TemplateSelectionProps {
  onClose: () => void;
}

export default function TemplateSelection({ onClose }: TemplateSelectionProps) {
  const setHoveredTemplate = useSetAtom(hoveredTemplatePreviewAtom);
  const { setModalDeepLink } = useModalDeepLink();
  const applyTemplate = useApplyTemplate();
  const setOpenSidePanel = useSetAtom(sidePanelType);

  const handleAiTemplateClick = () => {
    tracking.trackAnalyticsEvent("onboarding_cim_ai_template_selected");
    setHoveredTemplate(null);
    onClose();
    setOpenSidePanel(SidepanelType.aiPanel);
  };

  const handleClose = () => {
    tracking.trackAnalyticsEvent("onboarding_cim_templates_closed");
    onClose();
  };

  const handleTemplateClick = async (templateId: string) => {
    tracking.trackAnalyticsEvent("onboarding_cim_template_selected", { template_name: templateId });
    setHoveredTemplate(null);
    onClose();
    applyTemplate(templateId);
  };

  const handleMoreTemplates = () => {
    tracking.trackAnalyticsEvent("onboarding_cim_templates_more_clicked");
    setModalDeepLink({ modalType: ModalType.TemplateLibrary });
    onClose();
  };

  const handleTemplateHover = async (templateId: string | null) => {
    if (!templateId) {
      setHoveredTemplate(null);
      return;
    }

    const templateData = await loadTemplate(templateId);

    if (templateData) {
      setHoveredTemplate({
        templateId,
        templateData,
        boundaries: TEMPLATE_BOUNDARIES,
      });
    } else {
      setHoveredTemplate(null);
    }
  };

  useEffect(() => {
    tracking.trackAnalyticsEvent("onboarding_cim_templates_viewed");

    return () => {
      setHoveredTemplate(null);
    };
  }, [setHoveredTemplate]);

  useLoadTemplates();

  return (
    <div className={styles.modal} id="onboarding-cim-templates-modal" onClick={(e) => e.stopPropagation()}>
      <CloseIcon color="#848199" width={8} height={8} className={styles.closeButton} onClick={handleClose} />

      <div className={styles.header}>
        <h2 className={styles.title}>Start visualizing your work</h2>
        <p className={styles.description}>Pick a template that suits your workflow</p>
        <button className={styles.moreTemplatesButton} onClick={handleMoreTemplates}>
          {templatesIcon}
          More Templates
        </button>
      </div>

      <div className={styles.templatesGrid}>
        {TEMPLATE_SELECTION_CARDS.map((template) => (
          <TemplateSelectionCard
            key={template.id}
            template={template}
            handleTemplateClick={handleTemplateClick}
            handleTemplateHover={handleTemplateHover}
          />
        ))}
        <AISelectionCard onClick={handleAiTemplateClick} />
      </div>
    </div>
  );
}
